import i18n from '@/utils/i18n';
import React, { useEffect } from 'react';
import { AlertDescription } from './alerts/alert-description';
import { AlertTable } from './alerts/alert-table';
import { AnalyticEvent } from '@/analytics/fennel/registry';
import { AnalyticsAlertResponse } from './types';
import { AnalyticsAlertStatusType, AnalyticsAlertType } from '@/types';
import { BaseActionSheet } from '@/components/base-action-sheet';
import { Button } from '@/components/button';
import {
  Dimensions,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { fennelTrack } from '@/analytics/fennel/hook';
import { formatCurrency } from '@/utils/money';
import { formatDate } from '@/utils/date';
import { getAlertUUIDs } from '@/hooks/use-analytics-alerts-data';
import { getTranslationValue } from '@/utils/translations';
import { LostItemSupportInfo } from '@/components/insights-hub/lost-item-support-info';
import { TYPOGRAPHY } from '@/styles';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';

interface AnalyticsActionSheetProps {
  alert: AnalyticsAlertResponse;
  onAction: (
    alert: AnalyticsAlertResponse,
    actionType: AnalyticsAlertStatusType,
  ) => void;
  onClose: () => void;
  onSupportInfoPress?: (alert: AnalyticsAlertResponse) => void;
}

export const AnalyticsActionSheet: React.FC<AnalyticsActionSheetProps> = ({
  alert,
  onAction,
  onClose,
  onSupportInfoPress,
}) => {
  let content = null;

  const { currencyCode } = useVariantPackInfoContext();

  useEffect(() => {
    const alertUUIDs = getAlertUUIDs(alert);
    const trackingData = {
      alertType: alert.alert_type,
      alertUUIDs,
      componentName: 'AnalyticsActionSheet',
      restaurantUUID: alert.restaurant_uuid,
    };

    fennelTrack(AnalyticEvent.SALES_ALERT_VIEW, trackingData);
  }, [alert]);

  if (alert.alert_type === AnalyticsAlertType.OverduePayment) {
    content = (
      <ScrollView style={{ maxHeight: Dimensions.get('window').height * 0.3 }}>
        <AlertTable
          columns={[
            {
              key: 'invoice_number',
              title: i18n.t('dsr_analytics.alerts.overdue_payment.invoice'),
            },
            {
              key: 'due_at',
              render: (value: string | number) => formatDate(String(value)),
              title: i18n.t('dsr_analytics.alerts.overdue_payment.due_date'),
            },
            {
              key: 'amount',
              render: (value) => formatCurrency(value as number, currencyCode),
              title: i18n.t('dsr_analytics.alerts.overdue_payment.amount'),
            },
          ]}
          data={alert.data}
        />
      </ScrollView>
    );
  } else if (alert.alert_type === AnalyticsAlertType.LostItem) {
    content = (
      <TouchableOpacity
        onPress={() => {
          onSupportInfoPress?.(alert);
          onClose();
        }}
      >
        <LostItemSupportInfo
          displayName={alert.display_name}
          externalID={alert.external_item_id}
          packUnit={alert.pack_unit}
          photoURLs={alert.photo_url_list}
        />
      </TouchableOpacity>
    );
  } else {
    content = <AlertDescription text={getTranslationValue(alert.subtitle)} />;
  }

  return (
    <BaseActionSheet
      contentStyle={styles.modalContainer}
      isVisible
      onClose={onClose}
      showCloseButton={false}
    >
      <Text style={[TYPOGRAPHY.h2, styles.title]}>
        {getTranslationValue(alert.title)}
      </Text>
      {/* Churn Risk alerts do not have a subtitle */}
      {alert.alert_type !== AnalyticsAlertType.ChurnRisk && alert.subtitle ? (
        <Text style={[TYPOGRAPHY.p, styles.subtitle]}>
          {getTranslationValue(alert.subtitle)}
        </Text>
      ) : null}
      <View style={styles.content}>{content}</View>
      <View style={styles.buttonParentContainer}>
        <View style={styles.buttonContainer}>
          <Button
            onPress={() => {
              onClose();
              // tracking event triggered in onAction callback
              onAction(alert, AnalyticsAlertStatusType.Hidden);
            }}
            trackingData={null}
            type="primary"
          >
            {i18n.t('hide')}
          </Button>
          <Button
            onPress={() => {
              onClose();
              // tracking event triggered in onAction callback
              onAction(alert, AnalyticsAlertStatusType.Delete);
            }}
            trackingData={null}
            type="danger"
          >
            {i18n.t('remove')}
          </Button>
        </View>
      </View>
    </BaseActionSheet>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    marginBottom: 16,
  },
  buttonParentContainer: {
    paddingTop: 24,
  },
  content: {
    paddingTop: 18,
  },
  modalContainer: {
    maxHeight: '100%',
  },
  subtitle: {
    lineHeight: TYPOGRAPHY.p.lineHeight * 1.25,
  },
  title: {
    paddingBottom: 4,
  },
});
